import type {
  AspectEdge,
  AspectName,
  AspectSpec,
  BirthChart,
  HydratedNode,
  HydratedPlanet,
  ZodiacPosition,
} from "../astrology";
import { GLYPH_FILL } from "./astro-glyph-assets";
import {
  ANGLE_GLYPHS,
  PLANET_GLYPHS,
  ZODIAC_GLYPHS,
  getAstroGlyph,
} from "./astro-glyph-registry";
import type {
  AstroGlyphDefinition,
  AstroGlyphPrimitive,
  AstroWheelPlanetGlyphKey,
  AstroWheelZodiacSign,
} from "./astro-glyph-types";

export type { AspectSpec };

export interface AstroWheelViewBox {
  minX?: number;
  minY?: number;
  width: number;
  height: number;
}

export type AstroWheelElement = "fire" | "earth" | "air" | "water";

export interface AstroWheelPaletteOverrides {
  ringStroke?: string;
  label?: string;
  subtle?: string;
  zodiacStroke?: string;
  zodiacGlyph?: string;
  houseLine?: string;
  houseLabel?: string;
  angleLine?: string;
  angleLabel?: string;
  planetGlyph?: string;
  planetTick?: string;
  glyphHalo?: string;
  aspectGuide?: string;
  signColors?: Partial<Record<AstroWheelZodiacSign, string>>;
  elementColors?: Partial<Record<AstroWheelElement, string>>;
  aspects?: Partial<Record<AspectName | (string & {}), string>>;
}

export type AstroWheelPalette = "default" | "monochrome" | AstroWheelPaletteOverrides;

export interface AstroWheelAspectOptions {
  enabled?: boolean;
  /**
   * Explicit aspect edges to render. When omitted, the renderer uses
   * `chart.aspects`; if `aspectSpecs` is supplied it recomputes planet aspects.
   */
  edges?: readonly AspectEdge[];
  /** Recompute aspects from chart planets with caller-controlled orbs. */
  aspectSpecs?: readonly AspectSpec[];
}

export interface AstroWheelZodiacOptions {
  segments?: boolean;
  glyphs?: boolean;
  ticks?: boolean;
}

export interface AstroWheelHouseOptions {
  labels?: boolean;
  cuspLines?: boolean;
  angles?: boolean;
}

export type AstroWheelPointConnectorMode = "auto" | "always" | "never";

export interface AstroWheelLayoutOptions {
  rings?: Partial<Record<AstroWheelRing["id"], number>>;
  pointConnectors?: AstroWheelPointConnectorMode;
  maxPointDisplacementDegrees?: number;
}

export interface AstroWheelPointOptions {
  enabled?: boolean;
  nodes?: boolean;
  vertex?: boolean;
  collisionThresholdDegrees?: number;
}

export interface AstroWheelPointSource {
  key?: string;
  name: string;
  longitude: number;
  kind?: AstroWheelPointKind;
  glyph?: string;
  retrograde?: boolean;
  zodiacPosition?: ZodiacPosition;
}

export interface AstroWheelPointLayerInput {
  id: string;
  label?: string;
  chart?: BirthChart;
  points?: readonly AstroWheelPointSource[];
  color?: string;
  tickColor?: string;
  radius?: number | "inner" | "base" | "outer" | "external";
  radiusOffset?: number;
  glyphScale?: number;
  collisionThresholdDegrees?: number;
  nodes?: boolean;
  vertex?: boolean;
}

export interface AstroWheelAspectLayerInput {
  id: string;
  label?: string;
  chart?: BirthChart;
  edges?: readonly AspectEdge[];
  aspectSpecs?: readonly AspectSpec[];
  pointLayerId?: string;
  color?: string;
  colors?: Partial<Record<AspectName | (string & {}), string>>;
  radius?: number;
  radiusScale?: number;
  strokeWidth?: number;
  opacityScale?: number;
  pointLayerIdA?: string;
  pointLayerIdB?: string;
}

export interface AstroWheelSvgOptions {
  width?: number | string;
  height?: number | string;
  viewBox?: AstroWheelViewBox;
  background?: string | "transparent";
  palette?: AstroWheelPalette;
  zodiac?: boolean | AstroWheelZodiacOptions;
  aspects?: boolean | AstroWheelAspectOptions;
  houses?: boolean | AstroWheelHouseOptions;
  points?: boolean | AstroWheelPointOptions;
  pointLayers?: readonly AstroWheelPointLayerInput[];
  aspectLayers?: readonly AstroWheelAspectLayerInput[];
  excludeBodies?: readonly string[];
  padding?: number;
  title?: string;
  layout?: AstroWheelLayoutOptions;
}

export interface AstroWheelCoordinate {
  x: number;
  y: number;
}

export interface AstroWheelLabelBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AstroWheelLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface AstroWheelArcConnector {
  startAngle: number;
  endAngle: number;
  radius: number;
  path: string;
}

export interface AstroWheelRing {
  id: "aspects" | "planets" | "zodiac" | "houses";
  r1: number;
  r2: number;
}

export interface AstroWheelZodiacSegment {
  sign: AstroWheelZodiacSign;
  glyph: string;
  element: AstroWheelElement;
  longitudeStart: number;
  longitudeEnd: number;
  angleStart: number;
  angleEnd: number;
  path: string;
  fill: string;
  labelPosition: AstroWheelCoordinate;
  labelFontSize: number;
}

export interface AstroWheelHouseCusp {
  house: number;
  longitude: number;
  sign: string;
  line: AstroWheelLine;
  labelPosition: AstroWheelCoordinate;
  labelFontSize: number;
}

export interface AstroWheelAngleMarker {
  key: "ASC" | "MC" | "DSC" | "IC";
  longitude: number;
  zodiacPosition: ZodiacPosition;
  line: AstroWheelLine;
  labelPosition: AstroWheelCoordinate;
  labelFontSize: number;
  labelRotation: number;
}

export type AstroWheelPointKind = "planet" | "node" | "vertex";

export interface AstroWheelPoint {
  layerId: string;
  key: string;
  name: string;
  kind: AstroWheelPointKind;
  glyph: string;
  glyphKey?: string;
  longitude: number;
  displayLongitude: number;
  zodiacPosition?: ZodiacPosition;
  tickLine: AstroWheelLine;
  leaderArc?: AstroWheelArcConnector;
  leaderLine?: AstroWheelLine;
  glyphPosition: AstroWheelCoordinate;
  labelAngle: number;
  glyphFontSize: number;
  retrograde?: boolean;
  color: string;
  tickColor: string;
  labelBounds?: AstroWheelLabelBounds;
}

export interface AstroWheelAspectLine {
  layerId: string;
  planetA: string;
  planetB: string;
  planetAKey: string;
  planetBKey: string;
  aspect: AspectName | (string & {});
  aspectAngle: number;
  delta: number;
  orb: number;
  color: string;
  opacity: number;
  line: AstroWheelLine;
  strokeWidth: number;
}

export interface AstroWheelPointLayer {
  id: string;
  label?: string;
  color: string;
  tickColor: string;
  radius: number;
  points: readonly AstroWheelPoint[];
}

export interface AstroWheelAspectLayer {
  id: string;
  label?: string;
  aspectLines: readonly AstroWheelAspectLine[];
}

export interface AstroWheelRenderModel {
  viewBox: Required<AstroWheelViewBox>;
  center: AstroWheelCoordinate;
  outerRadius: number;
  scale: number;
  ascendantLongitude: number;
  rings: Record<AstroWheelRing["id"], AstroWheelRing>;
  zodiacSegments: readonly AstroWheelZodiacSegment[];
  houseCusps: readonly AstroWheelHouseCusp[];
  angleMarkers: readonly AstroWheelAngleMarker[];
  points: readonly AstroWheelPoint[];
  pointByKey: Record<string, AstroWheelPoint>;
  aspectLines: readonly AstroWheelAspectLine[];
  pointLayers: readonly AstroWheelPointLayer[];
  aspectLayers: readonly AstroWheelAspectLayer[];
  palette: ResolvedAstroWheelPalette;
}

interface ResolvedZodiacOptions {
  segments: boolean;
  glyphs: boolean;
  ticks: boolean;
}

interface ResolvedHouseOptions {
  labels: boolean;
  cuspLines: boolean;
  angles: boolean;
}

interface ResolvedAspectOptions {
  enabled: boolean;
  edges?: readonly AspectEdge[];
  aspectSpecs?: readonly AspectSpec[];
}

export interface ResolvedAstroWheelPalette {
  ringStroke: string;
  label: string;
  subtle: string;
  zodiacStroke: string;
  zodiacGlyph: string;
  houseLine: string;
  houseLabel: string;
  angleLine: string;
  angleLabel: string;
  planetGlyph: string;
  planetTick: string;
  glyphHalo: string;
  aspectGuide: string;
  signColors: Partial<Record<AstroWheelZodiacSign, string>>;
  elementColors: Record<AstroWheelElement, string>;
  aspects: Record<string, string>;
}

interface ResolvedPointOptions {
  enabled: boolean;
  nodes: boolean;
  vertex: boolean;
  collisionThresholdDegrees?: number;
}

const TAU = Math.PI * 2;
const GLYPH_OUTLINE_FILTER_ID = "astro-wheel-glyph-outline";
const DEFAULT_ASTRO_WHEEL_RING_FRACTIONS: Record<AstroWheelRing["id"], number> = {
  houses: 7,
  zodiac: 13,
  planets: 27,
  aspects: 53,
};
const DEFAULT_POINT_DISPLACEMENT_DEGREES = 20;

export const ASTRO_WHEEL_DEFAULT_VIEWBOX: Required<AstroWheelViewBox> = {
  minX: 0,
  minY: 0,
  width: 600,
  height: 600,
};

export const ASTRO_WHEEL_DEFAULT_ASPECT_SPECS = [
  { name: "conjunction", angle: 0, orb: 8 },
  { name: "duodecile", angle: 30, orb: 2 },
  { name: "octile", angle: 45, orb: 3 },
  { name: "sextile", angle: 60, orb: 5 },
  { name: "square", angle: 90, orb: 6 },
  { name: "trine", angle: 120, orb: 7 },
  { name: "trioctile", angle: 135, orb: 3 },
  { name: "quincunx", angle: 150, orb: 3 },
  { name: "opposition", angle: 180, orb: 8 },
] as const satisfies readonly AspectSpec[];

export const ASTRO_WHEEL_ZODIAC_SIGNS = [
  { sign: "Aries", glyph: "♈", element: "fire" },
  { sign: "Taurus", glyph: "♉", element: "earth" },
  { sign: "Gemini", glyph: "♊", element: "air" },
  { sign: "Cancer", glyph: "♋", element: "water" },
  { sign: "Leo", glyph: "♌", element: "fire" },
  { sign: "Virgo", glyph: "♍", element: "earth" },
  { sign: "Libra", glyph: "♎", element: "air" },
  { sign: "Scorpio", glyph: "♏", element: "water" },
  { sign: "Sagittarius", glyph: "♐", element: "fire" },
  { sign: "Capricorn", glyph: "♑", element: "earth" },
  { sign: "Aquarius", glyph: "♒", element: "air" },
  { sign: "Pisces", glyph: "♓", element: "water" },
] as const satisfies readonly {
  sign: AstroWheelZodiacSign;
  glyph: string;
  element: AstroWheelElement;
}[];

export const ASTRO_WHEEL_POINT_GLYPHS: Record<string, string> = {
  sun: "☉",
  moon: "☽",
  mercury: "☿",
  venus: "♀",
  mars: "♂",
  jupiter: "♃",
  saturn: "♄",
  uranus: "♅",
  neptune: "♆",
  pluto: "♇",
  earth: "⊕",
  chiron: "⚷",
  "mean node": "☊",
  "true node": "☊",
  "north node": "☊",
  "south node": "☋",
  lilith: "⚸",
  "lilith mean": "⚸",
  "lilith true": "⚸",
  "black moon lilith": "⚸",
  "wheel of fortune": "⊗",
  "part of fortune": "⊗",
  "pars fortunae": "⊗",
  "lot of fortune": "⊗",
  ascendant: "AC",
  midheaven: "MC",
  descendant: "DC",
  nadir: "IC",
  vertex: "Vx",
  retrograde: "Rx",
};

const DEFAULT_PALETTE: ResolvedAstroWheelPalette = {
  ringStroke: "#1f2933",
  label: "#111827",
  subtle: "#9a9189",
  zodiacStroke: "#1f2933",
  zodiacGlyph: "#111827",
  houseLine: "#8f877f",
  houseLabel: "#111827",
  angleLine: "#0f172a",
  angleLabel: "#0f172a",
  planetGlyph: "#111827",
  planetTick: "#111827",
  glyphHalo: "#0a1628",
  aspectGuide: "#d7d0c6",
  elementColors: {
    fire: "#e27657",
    earth: "#c9bd72",
    air: "#78b985",
    water: "#6594d0",
  },
  signColors: {},
  aspects: {
    conjunction: "#6b7280",
    duodecile: "#9a8f7a",
    octile: "#f97316",
    sextile: "#2563eb",
    square: "#dc2626",
    trine: "#16a34a",
    trioctile: "#f97316",
    quincunx: "#7c3aed",
    opposition: "#dc2626",
  },
};

const MONOCHROME_PALETTE: ResolvedAstroWheelPalette = {
  ringStroke: "#222222",
  label: "#222222",
  subtle: "#9a9a9a",
  zodiacStroke: "#222222",
  zodiacGlyph: "#222222",
  houseLine: "#777777",
  houseLabel: "#222222",
  angleLine: "#111111",
  angleLabel: "#111111",
  planetGlyph: "#111111",
  planetTick: "#111111",
  glyphHalo: "#0a1628",
  aspectGuide: "#c9c9c9",
  elementColors: {
    fire: "#ececec",
    earth: "#d8d8d8",
    air: "#f5f5f5",
    water: "#e3e3e3",
  },
  signColors: {},
  aspects: {
    conjunction: "#666666",
    duodecile: "#999999",
    octile: "#777777",
    sextile: "#777777",
    square: "#555555",
    trine: "#777777",
    trioctile: "#777777",
    quincunx: "#888888",
    opposition: "#555555",
  },
};

export interface GlyphSvgOptions {
  size?: number;
  color?: string;
  strokeWidth?: number;
  fill?: "none" | string;
  background?: string | "transparent";
}

export function generateGlyphSvg(
  primitives: readonly AstroGlyphPrimitive[],
  options: GlyphSvgOptions = {}
): string {
  const size = options.size ?? 48;
  const color = options.color ?? "#111827";
  const sw = options.strokeWidth ?? 0.14;
  const fillVal = options.fill ?? "none";
  const bg = options.background ?? "transparent";
  const scale = size / 2;
  const cx = size / 2;
  const cy = size / 2;
  const lines: string[] = [];
  const push = (s: string) => lines.push(s);

  push(`<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">`);
  if (bg !== "transparent") {
    push(`<rect width="${size}" height="${size}" fill="${escapeAttr(bg)}"/>`);
  }
  const glyphFill = bg === "transparent" ? "#fff" : bg;
  push(`<g transform="translate(${cx} ${cy}) scale(${fmt(scale)})" color="${escapeAttr(color)}" fill="${escapeAttr(fillVal)}" stroke="${escapeAttr(color)}" stroke-width="${fmt(sw)}" stroke-linecap="round" stroke-linejoin="round">`);
  renderGlyphPrimitives(push, primitives, { color, fill: fillVal, glyphFill });
  push(`</g>`);
  push(`</svg>`);
  return lines.join("\n");
}

export function generateAstroGlyphSvg(
  keyOrGlyph: string | AstroGlyphDefinition,
  options: GlyphSvgOptions = {}
): string {
  const glyph = typeof keyOrGlyph === "string" ? getAstroGlyph(keyOrGlyph) : keyOrGlyph;
  if (!glyph) {
    throw new Error(`Unknown astrology glyph: ${String(keyOrGlyph)}`);
  }
  const svg = generateGlyphSvg(glyph.primitives, options);
  return svg.replace("<svg ", `<svg data-astro-glyph="${escapeAttr(glyph.key)}" data-astro-glyph-category="${glyph.category}" `);
}

export function getAstroWheelRenderModel(
  chart: BirthChart,
  options: AstroWheelSvgOptions = {}
): AstroWheelRenderModel {
  const viewBox = normalizeViewBox(options.viewBox);
  const palette = resolvePalette(options.palette, options.background ?? "transparent");
  const excludedBodies = resolveExcludedBodies(options.excludeBodies);
  const scale = Math.min(
    viewBox.width / ASTRO_WHEEL_DEFAULT_VIEWBOX.width,
    viewBox.height / ASTRO_WHEEL_DEFAULT_VIEWBOX.height
  );
  const padding = options.padding ?? round(18 * scale + getExternalPointPadding(options, scale));
  const center = {
    x: round(viewBox.minX + viewBox.width / 2),
    y: round(viewBox.minY + viewBox.height / 2),
  };
  const outerRadius = round(Math.max(0, Math.min(viewBox.width, viewBox.height) / 2 - padding));
  const angleOf = (longitude: number) =>
    deg2rad(clamp360(180 + chart.houses.ascendant.longitude - longitude));
  const rings = buildRings(outerRadius, options.layout?.rings);
  const pointConnectorMode = options.layout?.pointConnectors ?? "auto";
  const maxPointDisplacementDegrees = options.layout?.maxPointDisplacementDegrees
    ?? DEFAULT_POINT_DISPLACEMENT_DEGREES;
  const resolvedPoints = resolvePointOptions(options.points);
  const resolvedHouses = resolveHouseOptions(options.houses);
  const resolvedAspects = resolveAspectOptions(options.aspects);
  const zodiacSegments = buildZodiacSegments({ center, angleOf, rings, scale, palette });
  const houseCusps = resolvedHouses.cuspLines || resolvedHouses.labels
    ? buildHouseCusps({
        houses: chart.houses.houses,
        center,
        angleOf,
        rings,
        scale,
      })
    : [];
  const angleMarkers = resolvedHouses.angles
    ? buildAngleMarkers({
        chart,
        center,
        angleOf,
        rings,
        scale,
      })
    : [];
  const basePointLayer = buildPointLayer({
    layer: {
      id: "birth",
      label: "Birth",
      chart,
      color: palette.planetGlyph,
      tickColor: palette.planetTick,
      radius: "base",
      nodes: resolvedPoints.nodes,
      vertex: resolvedPoints.vertex,
      collisionThresholdDegrees: resolvedPoints.collisionThresholdDegrees,
    },
    center,
    angleOf,
    rings,
    scale,
    palette,
    excludedBodies,
    pointConnectorMode,
    maxDisplacementDegrees: maxPointDisplacementDegrees,
  });
  const extraPointLayers = (options.pointLayers ?? []).map((layer) =>
    buildPointLayer({
      layer,
      center,
      angleOf,
      rings,
      scale,
      palette,
      excludedBodies,
      pointConnectorMode,
      maxDisplacementDegrees: maxPointDisplacementDegrees,
    })
  );
  const pointLayers = [
    ...(resolvedPoints.enabled ? [basePointLayer] : []),
    ...extraPointLayers,
  ];
  const points = pointLayers.flatMap((layer) => [...layer.points]);
  const pointByKey = Object.fromEntries(points.map((point) => [point.key, point]));
  const baseAspectLayer = resolvedAspects.enabled
    ? buildAspectLayer({
        layer: {
          id: "birth",
          label: "Birth aspects",
          chart,
          edges: resolvedAspects.edges,
          aspectSpecs: resolvedAspects.aspectSpecs,
        },
        chart,
        pointByKey,
        center,
        angleOf,
        rings,
        palette,
        excludedBodies,
      })
    : null;
  const extraAspectLayers = (options.aspectLayers ?? []).map((layer) =>
    buildAspectLayer({
      layer,
      chart: layer.chart ?? chart,
      pointByKey,
      center,
      angleOf,
      rings,
      palette,
      excludedBodies,
    })
  );
  const aspectLayers = [
    ...(baseAspectLayer ? [baseAspectLayer] : []),
    ...extraAspectLayers,
  ];
  const aspectLines = aspectLayers.flatMap((layer) => [...layer.aspectLines]);

  return {
    viewBox,
    center,
    outerRadius,
    scale,
    ascendantLongitude: normalizeAngle(chart.houses.ascendant.longitude),
    rings,
    zodiacSegments,
    houseCusps,
    angleMarkers,
    points,
    pointByKey,
    aspectLines,
    pointLayers,
    aspectLayers,
    palette,
  };
}

export function generateAstroWheelSvg(
  chart: BirthChart,
  options: AstroWheelSvgOptions = {}
): string {
  const model = getAstroWheelRenderModel(chart, options);
  const viewBox = model.viewBox;
  const zodiacOptions = resolveZodiacOptions(options.zodiac);
  const houseOptions = resolveHouseOptions(options.houses);
  const background = options.background ?? "transparent";
  const lines: string[] = [];
  const push = (line: string) => lines.push(line);
  const svgAttributes = [
    `xmlns="http://www.w3.org/2000/svg"`,
    options.width !== undefined ? `width="${escapeAttr(String(options.width))}"` : "",
    options.height !== undefined ? `height="${escapeAttr(String(options.height))}"` : "",
    `viewBox="${fmt(viewBox.minX)} ${fmt(viewBox.minY)} ${fmt(viewBox.width)} ${fmt(viewBox.height)}"`,
    `preserveAspectRatio="xMidYMid meet"`,
    `role="img"`,
    `aria-label="${escapeAttr(options.title ?? "Astrological birth chart wheel")}"`,
  ].filter(Boolean);

  push(`<svg ${svgAttributes.join(" ")}>`);
  push(`<title>${escapeText(options.title ?? "Astrological birth chart wheel")}</title>`);

  renderSvgDefs(push, model);

  if (background !== "transparent") {
    push(
      `<rect x="${fmt(viewBox.minX)}" y="${fmt(viewBox.minY)}" width="${fmt(viewBox.width)}" height="${fmt(viewBox.height)}" fill="${escapeAttr(background)}"/>`
    );
  }

  renderAspects(push, model);
  renderZodiac(push, model, zodiacOptions);
  renderHouses(push, model, houseOptions);
  renderPlanets(push, model);
  renderGlyphOverlay(push, model);
  push(`</svg>`);

  return lines.join("\n");
}

function renderSvgDefs(
  push: (line: string) => void,
  model: AstroWheelRenderModel
) {
  push(`<defs>`);
  push(
    `<filter id="${GLYPH_OUTLINE_FILTER_ID}" x="-45%" y="-45%" width="190%" height="190%">`
  );
  push(
    `<feMorphology in="SourceAlpha" operator="dilate" radius="${fmt(1.75 * model.scale)}" result="dilated"/>`
  );
  push(
    `<feFlood flood-color="${escapeAttr(model.palette.glyphHalo)}" result="haloColor"/>`
  );
  push(
    `<feComposite in="haloColor" in2="dilated" operator="in" result="outline"/>`
  );
  push(`<feMerge>`);
  push(`<feMergeNode in="outline"/>`);
  push(`<feMergeNode in="SourceGraphic"/>`);
  push(`</feMerge>`);
  push(`</filter>`);
  push(`</defs>`);
}

function renderAspects(
  push: (line: string) => void,
  model: AstroWheelRenderModel
) {
  if (model.aspectLayers.length === 0) {
    return;
  }

  const guideRadius = model.rings.aspects.r2 * 0.96;
  const innerGuideRadius = guideRadius * 0.52;

  push(`<g id="astro-wheel-aspects" aria-label="aspects">`);

  push(
    `<circle cx="${fmt(model.center.x)}" cy="${fmt(model.center.y)}" r="${fmt(guideRadius)}" fill="none" stroke="${escapeAttr(model.palette.aspectGuide)}" stroke-opacity="0.9" stroke-width="${fmt(1.25 * model.scale)}"/>`
  );

  push(
    `<circle cx="${fmt(model.center.x)}" cy="${fmt(model.center.y)}" r="${fmt(innerGuideRadius)}" fill="none" stroke="${escapeAttr(model.palette.aspectGuide)}" stroke-opacity="0.28" stroke-width="${fmt(model.scale)}"/>`
  );

  for (const layer of model.aspectLayers) {
    push(`<g class="astro-wheel-aspect-layer" data-aspect-layer="${escapeAttr(layer.id)}"${layer.label ? ` aria-label="${escapeAttr(layer.label)}"` : ""}>`);

    for (const aspect of layer.aspectLines) {
      push(
        `<line data-aspect-layer="${escapeAttr(aspect.layerId)}" data-aspect="${escapeAttr(aspect.aspect)}" data-planet-a="${escapeAttr(aspect.planetA)}" data-planet-b="${escapeAttr(aspect.planetB)}" x1="${fmt(aspect.line.x1)}" y1="${fmt(aspect.line.y1)}" x2="${fmt(aspect.line.x2)}" y2="${fmt(aspect.line.y2)}" stroke="${escapeAttr(aspect.color)}" stroke-opacity="${fmt(aspect.opacity)}" stroke-width="${fmt(aspect.strokeWidth * model.scale)}" stroke-linecap="round"/>`
      );
    }

    push(`</g>`);
  }

  push(
    `<circle cx="${fmt(model.center.x)}" cy="${fmt(model.center.y)}" r="${fmt(4 * model.scale)}" fill="${escapeAttr(model.palette.aspectGuide)}" fill-opacity="0.75" stroke="none"/>`
  );

  push(`</g>`);
}

function renderZodiac(
  push: (line: string) => void,
  model: AstroWheelRenderModel,
  options: ResolvedZodiacOptions
) {
  if (!options.segments && !options.glyphs && !options.ticks) {
    return;
  }

  const tickEvery = 1;
  const { center, rings, palette, scale } = model;
  const thickness = rings.zodiac.r2 - rings.zodiac.r1;
  const angleOf = (longitude: number) =>
    deg2rad(clamp360(180 + model.ascendantLongitude - longitude));

  push(`<g id="astro-wheel-zodiac" aria-label="zodiac">`);
  if (options.segments) {
    for (const segment of model.zodiacSegments) {
      push(
        `<path data-zodiac-sign="${escapeAttr(segment.sign)}" data-zodiac-element="${escapeAttr(segment.element)}" d="${segment.path}" fill="${escapeAttr(segment.fill)}" fill-opacity="0.82" stroke="${escapeAttr(palette.zodiacStroke)}" stroke-width="${fmt(model.scale)}"/>`
      );
    }
  }

  if (options.glyphs) {
    for (const segment of model.zodiacSegments) {
      renderZodiacGlyph(push, segment, palette);
    }
  }

  if (options.ticks) {
    for (let index = 0; index < 360 / tickEvery; index++) {
      const longitude = index * tickEvery;
      const signDegree = longitude % 30;
      const angle = angleOf(longitude);

      const isSignBoundary = signDegree === 0;
      const isTen = signDegree % 10 === 0;
      const isFive = signDegree % 5 === 0;

      const length = isSignBoundary
        ? thickness * 0.58
        : isTen
          ? thickness * 0.42
          : isFive
            ? thickness * 0.29
            : thickness * 0.18;

      const strokeWidth = isSignBoundary
        ? 2.2 * scale
        : isTen
          ? 1.55 * scale
          : isFive
            ? 1.1 * scale
            : 0.75 * scale;

      const opacity = isSignBoundary ? 0.95 : isFive ? 0.82 : 0.62;

      const inner = polarToXY(center.x, center.y, rings.zodiac.r2 - length, angle);
      const outer = polarToXY(center.x, center.y, rings.zodiac.r2, angle);

      push(
        `<line data-zodiac-tick="${longitude}" x1="${fmt(inner.x)}" y1="${fmt(inner.y)}" x2="${fmt(outer.x)}" y2="${fmt(outer.y)}" stroke="${escapeAttr(palette.zodiacStroke)}" stroke-opacity="${fmt(opacity)}" stroke-width="${fmt(strokeWidth)}"/>`
      );
    }
  }

  push(`</g>`);
}

function renderZodiacGlyph(
  push: (line: string) => void,
  segment: AstroWheelZodiacSegment,
  palette: ResolvedAstroWheelPalette
) {
  const glyph = ZODIAC_GLYPHS[segment.sign];
  const glyphScale = segment.labelFontSize * 0.72;
  const strokeWidth = clamp(0.11 / Math.max(glyphScale / 10, 0.6), 0.08, 0.16);

  push(
    `<g class="astro-wheel-glyph" data-astro-glyph="${escapeAttr(glyph.key)}" data-astro-glyph-category="${glyph.category}" data-zodiac-glyph="${escapeAttr(segment.sign)}" data-zodiac-symbol="${escapeAttr(segment.glyph)}" transform="translate(${fmt(segment.labelPosition.x)} ${fmt(segment.labelPosition.y)}) scale(${fmt(glyphScale)})" color="${escapeAttr(palette.zodiacGlyph)}" fill="none" stroke="${escapeAttr(palette.zodiacGlyph)}" stroke-width="${fmt(strokeWidth)}" stroke-linecap="round" stroke-linejoin="round">`
  );
  push(`<title>${escapeText(`${segment.sign} ${segment.glyph}`)}</title>`);
  renderGlyphPrimitives(push, glyph.primitives, { color: palette.zodiacGlyph });
  push(`</g>`);
}

function renderHouses(
  push: (line: string) => void,
  model: AstroWheelRenderModel,
  options: ResolvedHouseOptions
) {
  if (!options.cuspLines && !options.labels && !options.angles) {
    return;
  }

  push(`<g id="astro-wheel-houses" aria-label="houses">`);
  if (options.cuspLines) {
    for (const cusp of model.houseCusps) {
      const isAngleHouse = cusp.house === 1 || cusp.house === 4 || cusp.house === 7 || cusp.house === 10;

      push(
        `<line data-house-cusp="${cusp.house}" x1="${fmt(cusp.line.x1)}" y1="${fmt(cusp.line.y1)}" x2="${fmt(cusp.line.x2)}" y2="${fmt(cusp.line.y2)}" stroke="${escapeAttr(model.palette.houseLine)}" stroke-opacity="${fmt(isAngleHouse ? 0.72 : 0.42)}" stroke-width="${fmt(isAngleHouse ? 1.8 * model.scale : 0.95 * model.scale)}"/>`
      );
    }
  }

  if (options.labels) {
    for (const cusp of model.houseCusps) {
      push(
        `<text data-house-label="${cusp.house}" x="${fmt(cusp.labelPosition.x)}" y="${fmt(cusp.labelPosition.y)}" font-family="${textFontFamily()}" font-size="${fmt(cusp.labelFontSize)}" text-anchor="middle" dominant-baseline="middle" fill="${escapeAttr(model.palette.houseLabel)}">${cusp.house}</text>`
      );
    }
  }

  if (options.angles) {
    for (const marker of model.angleMarkers) {
      push(`<g data-angle-marker="${marker.key}">`);
      push(
        `<line x1="${fmt(marker.line.x1)}" y1="${fmt(marker.line.y1)}" x2="${fmt(marker.line.x2)}" y2="${fmt(marker.line.y2)}" stroke="${escapeAttr(model.palette.angleLine)}" stroke-width="${fmt(2 * model.scale)}"/>`
      );
      push(`</g>`);
    }
  }
  push(`</g>`);
}

function renderPlanets(
  push: (line: string) => void,
  model: AstroWheelRenderModel
) {
  if (model.pointLayers.length === 0) {
    return;
  }

  push(`<g id="astro-wheel-planets" aria-label="planets">`);

  for (const layer of model.pointLayers) {
    renderPointLayerGuide(push, layer, model);
  }

  for (const layer of model.pointLayers) {
    push(`<g class="astro-wheel-point-layer" data-point-layer="${escapeAttr(layer.id)}"${layer.label ? ` aria-label="${escapeAttr(layer.label)}"` : ""}>`);

    for (const point of layer.points) {
      push(
        `<g class="astro-wheel-point" data-point-layer="${escapeAttr(point.layerId)}" data-point-key="${escapeAttr(point.key)}" data-point-name="${escapeAttr(point.name)}" data-point-kind="${point.kind}" data-longitude="${fmt(point.longitude)}">`
      );

      push(`<title>${escapeText(point.name)} ${fmt(point.longitude)}°</title>`);

      push(
        `<line x1="${fmt(point.tickLine.x1)}" y1="${fmt(point.tickLine.y1)}" x2="${fmt(point.tickLine.x2)}" y2="${fmt(point.tickLine.y2)}" stroke="${escapeAttr(point.tickColor)}" stroke-opacity="0.82" stroke-width="${fmt(model.scale)}" stroke-linecap="round"/>`
      );

      if (point.leaderArc) {
        push(
          `<path class="astro-wheel-point-leader" d="${escapeAttr(point.leaderArc.path)}" fill="none" stroke="${escapeAttr(point.tickColor)}" stroke-opacity="0.52" stroke-width="${fmt(0.9 * model.scale)}" stroke-linecap="round" stroke-linejoin="round"/>`
        );
      } else if (point.leaderLine) {
        push(
          `<line class="astro-wheel-point-leader" x1="${fmt(point.leaderLine.x1)}" y1="${fmt(point.leaderLine.y1)}" x2="${fmt(point.leaderLine.x2)}" y2="${fmt(point.leaderLine.y2)}" stroke="${escapeAttr(point.tickColor)}" stroke-opacity="0.52" stroke-width="${fmt(0.9 * model.scale)}" stroke-linecap="round"/>`
        );
      }

      push(`</g>`);
    }

    push(`</g>`);
  }

  push(`</g>`);
}

function renderPointLayerGuide(
  push: (line: string) => void,
  layer: AstroWheelPointLayer,
  model: AstroWheelRenderModel
) {
  const isExternal = layer.radius > model.rings.houses.r2 + model.scale;
  if (!isExternal) {
    return;
  }

  push(
    `<circle class="astro-wheel-external-point-ring" data-point-layer="${escapeAttr(layer.id)}" cx="${fmt(model.center.x)}" cy="${fmt(model.center.y)}" r="${fmt(layer.radius)}" fill="none" stroke="${escapeAttr(layer.tickColor)}" stroke-opacity="0.32" stroke-width="${fmt(1.15 * model.scale)}"/>`
  );
}

function renderGlyphOverlay(
  push: (line: string) => void,
  model: AstroWheelRenderModel
) {
  if (model.angleMarkers.length === 0 && model.pointLayers.length === 0) {
    return;
  }

  push(`<g id="astro-wheel-glyph-layer" aria-label="chart glyphs">`);

  if (model.angleMarkers.length > 0) {
    push(`<g id="astro-wheel-angle-glyphs" aria-label="angle glyphs">`);
    for (const marker of model.angleMarkers) {
      renderAngleGlyph(push, marker, model);
    }
    push(`</g>`);
  }

  if (model.pointLayers.length > 0) {
    push(`<g id="astro-wheel-point-glyphs" aria-label="planet glyphs">`);
    for (const layer of model.pointLayers) {
      push(`<g class="astro-wheel-point-glyph-layer" data-point-layer="${escapeAttr(layer.id)}"${layer.label ? ` aria-label="${escapeAttr(layer.label)} glyphs"` : ""}>`);
      for (const point of layer.points) {
        renderPointLabelGroup(push, point, model);
      }
      push(`</g>`);
    }
    push(`</g>`);
  }

  push(`</g>`);
}

function renderAngleGlyph(
  push: (line: string) => void,
  marker: AstroWheelAngleMarker,
  model: AstroWheelRenderModel
) {
  const label = angleDisplayLabel(marker.key);
  const position = formatInlinePosition(marker.zodiacPosition);

  const labelFontSize = marker.labelFontSize;
  const positionFontSize = clamp(marker.labelFontSize * 0.52, 5.5 * model.scale, 9 * model.scale);
  const signScale = positionFontSize * 0.88;
  const gap = clamp(3.2 * model.scale, 2.5, 5);

  const labelWidth = estimateTextWidth(label, labelFontSize);
  const degreeWidth = estimateTextWidth(position.degreesText, positionFontSize);
  const minutesWidth = estimateTextWidth(position.minutesText, positionFontSize);
  const totalWidth = labelWidth + gap + degreeWidth + gap + signScale + gap + minutesWidth;
  const startX = -totalWidth / 2;

  const labelX = startX;
  const degreeX = labelX + labelWidth + gap;
  const signX = degreeX + degreeWidth + gap + signScale / 2;
  const minutesX = signX + signScale / 2 + gap;

  push(
    `<g class="astro-wheel-angle-glyph-label" data-angle-marker="${marker.key}" data-longitude="${fmt(marker.longitude)}" transform="translate(${fmt(marker.labelPosition.x)} ${fmt(marker.labelPosition.y)}) rotate(${fmt(marker.labelRotation)})" filter="url(#${GLYPH_OUTLINE_FILTER_ID})">`
  );

  push(`<title>${escapeText(`${label} ${position.degreesText} ${position.sign} ${position.minutesText}`)}</title>`);

  push(
    `<text class="astro-wheel-angle-label" x="${fmt(labelX)}" y="0" font-family="${textFontFamily()}" font-size="${fmt(labelFontSize)}" text-anchor="start" dominant-baseline="middle" fill="${escapeAttr(model.palette.angleLabel)}" font-weight="700">${escapeText(label)}</text>`
  );

  push(
    `<text class="astro-wheel-angle-degree" x="${fmt(degreeX)}" y="0" font-family="${textFontFamily()}" font-size="${fmt(positionFontSize)}" text-anchor="start" dominant-baseline="middle" fill="${escapeAttr(model.palette.angleLabel)}">${escapeText(position.degreesText)}</text>`
  );

  renderInlineSignGlyph(
    push,
    position.sign,
    signX,
    0,
    signScale,
    model.palette.angleLabel
  );

  push(
    `<text class="astro-wheel-angle-minutes" x="${fmt(minutesX)}" y="0" font-family="${textFontFamily()}" font-size="${fmt(positionFontSize)}" text-anchor="start" dominant-baseline="middle" fill="${escapeAttr(model.palette.angleLabel)}">${escapeText(position.minutesText)}</text>`
  );

  push(`</g>`);
}

function angleDisplayLabel(key: AstroWheelAngleMarker["key"]) {
  if (key === "ASC") return "AC";
  if (key === "DSC") return "DC";
  return key;
}

function renderPointLabelGroup(
  push: (line: string) => void,
  point: AstroWheelPoint,
  model: AstroWheelRenderModel
) {
  const layout = getPointLabelLayout(point, model.scale);
  const boundsAttr = point.labelBounds
    ? ` data-label-bounds="${escapeAttr(formatLabelBounds(point.labelBounds))}"`
    : "";

  push(
    `<g class="astro-wheel-point-label" data-point-layer="${escapeAttr(point.layerId)}" data-point-key="${escapeAttr(point.key)}" data-point-name="${escapeAttr(point.name)}" data-point-kind="${point.kind}" data-longitude="${fmt(point.longitude)}"${boundsAttr} transform="translate(${fmt(point.glyphPosition.x)} ${fmt(point.glyphPosition.y)})" filter="url(#${GLYPH_OUTLINE_FILTER_ID})">`
  );

  push(`<title>${escapeText(pointLabelTitle(point))}</title>`);

  renderPointGlyph(push, point, layout.glyphX, layout.glyphY, layout.glyphScale, model.palette.glyphHalo);

  if (point.retrograde) {
    renderRetrogradeMarker(
      push,
      layout.retrogradeX,
      layout.retrogradeY,
      layout.retrogradeFontSize,
      point.color
    );
  }

  renderPointInlineDegreeLabel(push, point, layout);
  push(`</g>`);
}

function renderPointGlyph(
  push: (line: string) => void,
  point: AstroWheelPoint,
  x: number,
  y: number,
  glyphScale: number,
  glyphFill: string
) {
  const glyph = point.glyphKey ? getAstroGlyph(point.glyphKey) : resolvePointGlyph(point.name);

  if (glyph) {
    const strokeWidth = clamp(0.12 / Math.max(glyphScale / 10, 0.6), 0.1, 0.18);
    push(
      `<g class="astro-wheel-glyph" data-astro-glyph="${escapeAttr(glyph.key)}" data-astro-glyph-category="${glyph.category}" data-point-glyph="${escapeAttr(point.name)}" transform="translate(${fmt(x)} ${fmt(y)}) scale(${fmt(glyphScale)})" color="${escapeAttr(point.color)}" fill="none" stroke="${escapeAttr(point.color)}" stroke-width="${fmt(strokeWidth)}" stroke-linecap="round" stroke-linejoin="round">`
    );
    renderGlyphPrimitives(push, glyph.primitives, { color: point.color, glyphFill });
    push(`</g>`);
  } else {
    push(
      `<text class="astro-wheel-glyph" data-point-glyph="${escapeAttr(point.name)}" x="${fmt(x)}" y="${fmt(y)}" font-family="${symbolFontFamily()}" font-size="${fmt(point.glyphFontSize)}" text-anchor="middle" dominant-baseline="middle" fill="${escapeAttr(point.color)}">${escapeText(point.glyph)}</text>`
    );
  }
}

function renderRetrogradeMarker(
  push: (line: string) => void,
  x: number,
  y: number,
  fontSize: number,
  color: string
) {
  const glyph = resolvePointGlyph("retrograde");

  if (glyph) {
    const glyphScale = fontSize * 0.62;
    const strokeWidth = clamp(0.12 / Math.max(glyphScale / 10, 0.6), 0.1, 0.18);

    push(
      `<g class="astro-wheel-point-retrograde" data-astro-glyph="${escapeAttr(glyph.key)}" data-astro-glyph-category="${glyph.category}" transform="translate(${fmt(x)} ${fmt(y)}) scale(${fmt(glyphScale)})" color="${escapeAttr(color)}" fill="none" stroke="${escapeAttr(color)}" stroke-width="${fmt(strokeWidth)}" stroke-linecap="round" stroke-linejoin="round">`
    );
    renderGlyphPrimitives(push, glyph.primitives, { color });
    push(`</g>`);
    return;
  }

  push(
    `<text class="astro-wheel-point-retrograde" x="${fmt(x)}" y="${fmt(y)}" font-family="${textFontFamily()}" font-size="${fmt(fontSize)}" text-anchor="middle" dominant-baseline="middle" fill="${escapeAttr(color)}" font-weight="700">R</text>`
  );
}

function renderPointInlineDegreeLabel(
  push: (line: string) => void,
  point: AstroWheelPoint,
  layout: PointLabelLayout
) {
  if (!layout.position) {
    return;
  }

  push(
    `<text class="astro-wheel-point-degree" x="${fmt(layout.degreeX)}" y="${fmt(layout.degreeY)}" font-family="${textFontFamily()}" font-size="${fmt(layout.fontSize)}" text-anchor="start" dominant-baseline="middle" fill="${escapeAttr(point.color)}" fill-opacity="0.9">${escapeText(layout.position.degreesText)}</text>`
  );
  renderInlineSignGlyph(push, layout.position.sign, layout.signX, layout.signY, layout.signScale, point.color);
  push(
    `<text class="astro-wheel-point-minutes" x="${fmt(layout.minutesX)}" y="${fmt(layout.minutesY)}" font-family="${textFontFamily()}" font-size="${fmt(layout.fontSize)}" text-anchor="start" dominant-baseline="middle" fill="${escapeAttr(point.color)}" fill-opacity="0.9">${escapeText(layout.position.minutesText)}</text>`
  );
}

function renderInlineSignGlyph(
  push: (line: string) => void,
  sign: string,
  x: number,
  y: number,
  glyphScale: number,
  color: string
) {
  const glyph = ZODIAC_GLYPHS[sign as AstroWheelZodiacSign];

  if (glyph) {
    const strokeWidth = clamp(0.11 / Math.max(glyphScale / 10, 0.6), 0.08, 0.16);
    push(
      `<g class="astro-wheel-point-sign-glyph" data-astro-glyph="${escapeAttr(glyph.key)}" data-astro-glyph-category="${glyph.category}" data-zodiac-glyph="${escapeAttr(sign)}" transform="translate(${fmt(x)} ${fmt(y)}) scale(${fmt(glyphScale)})" color="${escapeAttr(color)}" fill="none" stroke="${escapeAttr(color)}" stroke-width="${fmt(strokeWidth)}" stroke-linecap="round" stroke-linejoin="round">`
    );
    renderGlyphPrimitives(push, glyph.primitives, { color });
    push(`</g>`);
    return;
  }

  push(
    `<text class="astro-wheel-point-sign-glyph" x="${fmt(x)}" y="${fmt(y)}" font-family="${symbolFontFamily()}" font-size="${fmt(glyphScale)}" text-anchor="middle" dominant-baseline="middle" fill="${escapeAttr(color)}">${escapeText(sign.charAt(0).toUpperCase())}</text>`
  );
}

interface PointLabelLayout {
  glyphScale: number;
  glyphX: number;
  glyphY: number;
  fontSize: number;
  degreeX: number;
  degreeY: number;
  signX: number;
  signY: number;
  signScale: number;
  minutesX: number;
  minutesY: number;
  retrogradeX: number;
  retrogradeY: number;
  retrogradeFontSize: number;
  boundsX: number;
  boundsY: number;
  width: number;
  height: number;
  position: PointInlinePosition | null;
}

interface PointInlinePosition {
  sign: string;
  degreesText: string;
  minutesText: string;
}

function getPointLabelLayout(point: AstroWheelPoint, scale: number): PointLabelLayout {
  const glyphScale = point.glyphFontSize * 0.66;
  const fontSize = clamp(point.glyphFontSize * 0.31, 5.5 * scale, 9 * scale);
  const signScale = fontSize * 0.82;
  const gap = clamp(3.2 * scale, 2.4, 5);
  const railGap = clamp(4.4 * scale, 3.2, 7);

  const retrogradeFontSize = clamp(fontSize * 0.74, 4.4 * scale, 7.2 * scale);
  const retrogradeTextWidth = point.retrograde
    ? estimateTextWidth("Rx", retrogradeFontSize)
    : 0;
  const retrogradeGap = point.retrograde ? clamp(1.7 * scale, 1.2, 3) : 0;

  const position = point.zodiacPosition ? formatInlinePosition(point.zodiacPosition) : null;
  const degreeWidth = position ? estimateTextWidth(position.degreesText, fontSize) : 0;
  const minutesWidth = position ? estimateTextWidth(position.minutesText, fontSize) : 0;

  const positionWidth = position
    ? degreeWidth + gap + signScale + gap + minutesWidth
    : 0;

  const positionHeight = position ? Math.max(fontSize, signScale) : 0;

  const inwardX = -Math.cos(point.labelAngle);
  const inwardY = -Math.sin(point.labelAngle);

  const glyphHalf = glyphScale / 2;

  const retrogradeCenterDistance = point.retrograde
    ? glyphHalf + retrogradeGap + retrogradeTextWidth / 2
    : 0;

  const retrogradeRailWidth = point.retrograde
    ? retrogradeGap + retrogradeTextWidth
    : 0;

  const positionCenterDistance = position
    ? glyphHalf + retrogradeRailWidth + railGap + positionWidth / 2
    : 0;

  const positionCenterX = inwardX * positionCenterDistance;
  const positionCenterY = inwardY * positionCenterDistance;
  const positionLeftX = positionCenterX - positionWidth / 2;

  const glyphX = 0;
  const glyphY = 0;

  const retrogradeX = inwardX * retrogradeCenterDistance;
  const retrogradeY = inwardY * retrogradeCenterDistance;

  const degreeX = positionLeftX;
  const degreeY = positionCenterY;
  const signX = degreeX + degreeWidth + gap + signScale / 2;
  const signY = positionCenterY;
  const minutesX = signX + signScale / 2 + gap;
  const minutesY = positionCenterY;

  const boxes = [
    {
      x: -glyphHalf,
      y: -glyphHalf,
      width: glyphScale,
      height: glyphScale,
    },
  ];

  if (point.retrograde) {
    boxes.push({
      x: retrogradeX - retrogradeTextWidth / 2,
      y: retrogradeY - retrogradeFontSize / 2,
      width: retrogradeTextWidth,
      height: retrogradeFontSize,
    });
  }

  if (position) {
    boxes.push({
      x: positionLeftX,
      y: positionCenterY - positionHeight / 2,
      width: positionWidth,
      height: positionHeight,
    });
  }

  const boundsX = Math.min(...boxes.map((box) => box.x));
  const boundsY = Math.min(...boxes.map((box) => box.y));
  const boundsMaxX = Math.max(...boxes.map((box) => box.x + box.width));
  const boundsMaxY = Math.max(...boxes.map((box) => box.y + box.height));

  return {
    glyphScale,
    glyphX,
    glyphY,
    fontSize,
    degreeX,
    degreeY,
    signX,
    signScale,
    signY,
    minutesX,
    minutesY,
    retrogradeX,
    retrogradeY,
    retrogradeFontSize,
    boundsX,
    boundsY,
    width: boundsMaxX - boundsX,
    height: boundsMaxY - boundsY,
    position,
  };
}

function formatInlinePosition(position: ZodiacPosition): PointInlinePosition {
  let degrees = Math.floor(position.decimalDegrees);
  let minutes = Math.round((position.decimalDegrees - degrees) * 60);

  if (minutes === 60) {
    degrees += 1;
    minutes = 0;
  }

  return {
    sign: position.sign,
    degreesText: `${degrees}°`,
    minutesText: `${String(minutes).padStart(2, "0")}'`,
  };
}

function pointLabelTitle(point: AstroWheelPoint) {
  const position = point.zodiacPosition ? formatInlinePosition(point.zodiacPosition) : null;
  if (!position) {
    return `${point.name} ${fmt(point.longitude)}°`;
  }

  const retrograde = point.retrograde ? " Rx" : "";
  return `${point.name} ${position.degreesText} ${position.sign} ${position.minutesText}${retrograde}`;
}

function estimateTextWidth(value: string, fontSize: number) {
  return value.length * fontSize * 0.56;
}

function labelBoundsFor(
  position: AstroWheelCoordinate,
  layout: Pick<PointLabelLayout, "boundsX" | "boundsY" | "width" | "height">,
  padding: number
): AstroWheelLabelBounds {
  const width = layout.width + padding * 2;
  const height = layout.height + padding * 2;

  return {
    x: round(position.x + layout.boundsX - padding),
    y: round(position.y + layout.boundsY - padding),
    width: round(width),
    height: round(height),
  };
}

function constrainLabelInsideCircle(params: {
  center: AstroWheelCoordinate;
  position: AstroWheelCoordinate;
  layout: Pick<PointLabelLayout, "boundsX" | "boundsY" | "width" | "height">;
  padding: number;
  maxRadius: number;
}): {
  position: AstroWheelCoordinate;
  bounds: AstroWheelLabelBounds;
  moved: boolean;
} {
  const { center, layout, padding, maxRadius } = params;

  if (!Number.isFinite(maxRadius)) {
    return {
      position: params.position,
      bounds: labelBoundsFor(params.position, layout, padding),
      moved: false,
    };
  }

  let position = params.position;
  let moved = false;

  for (let attempt = 0; attempt < 6; attempt++) {
    const bounds = labelBoundsFor(position, layout, padding);
    const overflow = labelBoundsCircleOverflow(center, bounds, maxRadius);

    if (overflow <= 0) {
      return { position, bounds, moved };
    }

    const dx = position.x - center.x;
    const dy = position.y - center.y;
    const distance = Math.max(Math.hypot(dx, dy), 1);

    position = {
      x: round(position.x - (dx / distance) * (overflow + 0.75)),
      y: round(position.y - (dy / distance) * (overflow + 0.75)),
    };
    moved = true;
  }

  return {
    position,
    bounds: labelBoundsFor(position, layout, padding),
    moved,
  };
}

function labelBoundsCircleOverflow(
  center: AstroWheelCoordinate,
  bounds: AstroWheelLabelBounds,
  maxRadius: number
) {
  const corners = [
    { x: bounds.x, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y },
    { x: bounds.x, y: bounds.y + bounds.height },
    { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
  ];

  const farthestCorner = Math.max(
    ...corners.map((corner) =>
      Math.hypot(corner.x - center.x, corner.y - center.y)
    )
  );

  return farthestCorner - maxRadius;
}

function formatLabelBounds(bounds: AstroWheelLabelBounds) {
  return `${fmt(bounds.x)} ${fmt(bounds.y)} ${fmt(bounds.width)} ${fmt(bounds.height)}`;
}

function projectedLabelWidthAlongTangent(
  angleRad: number,
  bounds: Pick<AstroWheelLabelBounds, "width" | "height">
) {
  const tangentX = -Math.sin(angleRad);
  const tangentY = Math.cos(angleRad);
  return Math.abs(tangentX) * bounds.width + Math.abs(tangentY) * bounds.height;
}

const POINT_GLYPH_ALIASES: Record<string, AstroWheelPlanetGlyphKey> = {
  "mean node": "north node",
  "true node": "north node",
  "ascending node": "north node",
  "north lunar node": "north node",
  "descending node": "south node",
  "south lunar node": "south node",
  "wheel of fortune": "pars fortunae",
  "part of fortune": "pars fortunae",
  "lot of fortune": "pars fortunae",
  "fortune": "pars fortunae",
  "black moon lilith": "lilith",
  "mean lilith": "lilith",
  "true lilith": "lilith",
  "lilith mean": "lilith",
  "lilith true": "lilith",
  "asc": "ascendant",
  "ac": "ascendant",
  "mc": "midheaven",
  "medium coeli": "midheaven",
  "dsc": "descendant",
  "dc": "descendant",
  "ic": "nadir",
  "imum coeli": "nadir",
  "vx": "vertex",
  "vtx": "vertex",
  "rx": "retrograde",
};

function resolvePointGlyph(name: string): AstroGlyphDefinition | null {
  const key = name.trim().toLocaleLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  const resolved = POINT_GLYPH_ALIASES[key] ?? key;
  return PLANET_GLYPHS[resolved as AstroWheelPlanetGlyphKey] ?? ANGLE_GLYPHS[resolved as keyof typeof ANGLE_GLYPHS] ?? null;
}

function getExternalPointPadding(options: AstroWheelSvgOptions, scale: number) {
  const externalLayers = options.pointLayers?.filter((layer) => layer.radius === "external") ?? [];
  if (externalLayers.length === 0) {
    return 0;
  }

  return Math.max(
    ...externalLayers.map((layer) => (layer.radiusOffset ?? 18) + 36 * scale)
  );
}

function buildZodiacSegments(params: {
  center: AstroWheelCoordinate;
  angleOf: (longitude: number) => number;
  rings: Record<AstroWheelRing["id"], AstroWheelRing>;
  scale: number;
  palette: ResolvedAstroWheelPalette;
}): AstroWheelZodiacSegment[] {
  const { center, angleOf, rings, scale, palette } = params;
  const boundaries = unwrapAngles(
    Array.from({ length: ASTRO_WHEEL_ZODIAC_SIGNS.length + 1 }, (_, index) =>
      angleOf(index * 30)
    ),
    "dec"
  );
  const thickness = rings.zodiac.r2 - rings.zodiac.r1;
  const labelFontSize = clamp(thickness * 0.58, 12 * scale, 28 * scale);

  return ASTRO_WHEEL_ZODIAC_SIGNS.map((entry, index) => {
    const angleStart = boundaries[index];
    const angleEnd = boundaries[index + 1];
    const labelPosition = polarToXY(
      center.x,
      center.y,
      (rings.zodiac.r1 + rings.zodiac.r2) / 2,
      angleOf(index * 30 + 15)
    );
    return {
      sign: entry.sign,
      glyph: entry.glyph,
      element: entry.element,
      longitudeStart: index * 30,
      longitudeEnd: index === 11 ? 360 : (index + 1) * 30,
      angleStart,
      angleEnd,
      path: arcPath(center.x, center.y, rings.zodiac.r1, rings.zodiac.r2, angleStart, angleEnd),
      fill: palette.signColors[entry.sign] ?? palette.elementColors[entry.element],
      labelPosition,
      labelFontSize,
    };
  });
}

function buildHouseCusps(params: {
  houses: readonly ZodiacPosition[];
  center: AstroWheelCoordinate;
  angleOf: (longitude: number) => number;
  rings: Record<AstroWheelRing["id"], AstroWheelRing>;
  scale: number;
}): AstroWheelHouseCusp[] {
  const { houses, center, angleOf, rings, scale } = params;
  const ring = rings.houses;
  const spokeInnerRadius = rings.aspects.r2;
  const spokeOuterRadius = ring.r2 + 4 * scale;
  const labelRadius = ring.r2 + 12 * scale;
  const labelFontSize = clamp((ring.r2 - ring.r1) * 0.48, 10 * scale, 19 * scale);

  return houses.map((house, index) => {
    const angle = angleOf(house.longitude);
    const next = houses[(index + 1) % houses.length];
    const span = clamp360(next.longitude - house.longitude);
    const labelLongitude = clamp360(house.longitude + span / 2);

    return {
      house: index + 1,
      longitude: normalizeAngle(house.longitude),
      sign: house.sign,
      line: lineFromPolar(center, spokeInnerRadius, spokeOuterRadius, angle),
      labelPosition: polarToXY(center.x, center.y, labelRadius, angleOf(labelLongitude)),
      labelFontSize,
    };
  });
}

function buildAngleMarkers(params: {
  chart: BirthChart;
  center: AstroWheelCoordinate;
  angleOf: (longitude: number) => number;
  rings: Record<AstroWheelRing["id"], AstroWheelRing>;
  scale: number;
}): AstroWheelAngleMarker[] {
  const { chart, center, angleOf, rings, scale } = params;
  const ring = rings.houses;
  const labelFontSize = clamp((ring.r2 - ring.r1) * 0.32, 8 * scale, 18 * scale);
  const labelRadius = Math.min(
    ring.r2 + 9 * scale,
    ring.r2 - Math.max(10 * scale, labelFontSize * 0.6)
  );

  const markers = [
    { key: "ASC" as const, position: chart.houses.ascendant },
    { key: "MC" as const, position: chart.houses.mc },
    { key: "DSC" as const, position: chart.houses.dc },
    { key: "IC" as const, position: chart.houses.ic },
  ];

  return markers.map((marker) => {
    const angle = angleOf(marker.position.longitude);
    const angleDeg = clamp360(angle * (180 / Math.PI));
    let labelRotation = angleDeg;
    if (labelRotation > 90 && labelRotation < 270) {
      labelRotation -= 180;
    }

    return {
      key: marker.key,
      longitude: normalizeAngle(marker.position.longitude),
      zodiacPosition: marker.position,
      line: lineFromPolar(center, ring.r1 + 4 * scale, ring.r2 - 4 * scale, angle),
      labelPosition: polarWithNudge(
        center.x,
        center.y,
        labelRadius,
        angle,
        0,
        0
      ),
      labelFontSize,
      labelRotation,
    };
  });
}

function buildPointLayer(params: {
  layer: AstroWheelPointLayerInput;
  center: AstroWheelCoordinate;
  angleOf: (longitude: number) => number;
  rings: Record<AstroWheelRing["id"], AstroWheelRing>;
  scale: number;
  palette: ResolvedAstroWheelPalette;
  excludedBodies: ReadonlySet<string>;
  pointConnectorMode: AstroWheelPointConnectorMode;
  maxDisplacementDegrees: number;
}): AstroWheelPointLayer {
  const {
    layer,
    center,
    angleOf,
    rings,
    scale,
    palette,
    excludedBodies,
    pointConnectorMode,
    maxDisplacementDegrees,
  } = params;
  const ring = rings.planets;
  const requestedRadius = resolvePointLayerRadius(layer, rings);
  const requestedExternalLayer = requestedRadius > rings.houses.r2 + scale;

  const thickness = ring.r2 - ring.r1;
  const glyphFontSize = clamp(thickness * 0.68, 14 * scale, 30 * scale) * (layer.glyphScale ?? 1);

  const autoPlanetRail = layer.radius === undefined || layer.radius === "base";
  const radius = !requestedExternalLayer && autoPlanetRail
    ? clamp(
        ring.r2 - glyphFontSize * 0.58 - Math.max(3 * scale, 2),
        ring.r1 + glyphFontSize * 0.65,
        ring.r2 - glyphFontSize * 0.48
      )
    : requestedRadius;

  const isExternalLayer = radius > rings.houses.r2 + scale;
  const tickLength = Math.min(14 * scale, thickness * 0.34);

  const collisionPadding = Math.max(2 * scale, 1.5);
  const zodiacKeepOutGap = Math.max(5 * scale, 4);
  const labelMaxRadius = isExternalLayer
    ? Number.POSITIVE_INFINITY
    : rings.zodiac.r1 - zodiacKeepOutGap;
  const seeds = getPointSeeds(layer, excludedBodies);
  const color = layer.color ?? palette.planetGlyph;
  const tickColor = layer.tickColor ?? color;
  const pointIntents = seeds.map((seed) => {
    const tickAngle = angleOf(seed.longitude);
    const pointGlyphFontSize = seed.kind === "vertex"
      ? glyphFontSize * 0.82
      : glyphFontSize;

    const rawGlyphRadius = radius;
    const glyphRadius = isExternalLayer
      ? rawGlyphRadius
      : clamp(
          rawGlyphRadius,
          rings.aspects.r2 + pointGlyphFontSize * 0.65,
          labelMaxRadius - pointGlyphFontSize * 0.5
        );

    const connectorRadius = glyphRadius;
    const tickLine = isExternalLayer
      ? lineFromPolar(
          center,
          rings.houses.r2 + 2 * scale,
          connectorRadius,
          tickAngle
        )
      : lineFromPolar(
          center,
          Math.min(ring.r2, connectorRadius + tickLength),
          connectorRadius,
          tickAngle
        );

    const idealPosition = polarToXY(center.x, center.y, glyphRadius, tickAngle);
    const point = makeAstroWheelPoint({
      layer,
      seed,
      tickLine,
      labelAngle: tickAngle,
      glyphPosition: idealPosition,
      glyphFontSize: pointGlyphFontSize,
      color,
      tickColor,
    });
    const layout = getPointLabelLayout(point, scale);
    const labelBounds = labelBoundsFor(idealPosition, layout, collisionPadding);
    const labelTangentDemandPx = projectedLabelWidthAlongTangent(tickAngle, labelBounds);
    const configuredTangentDemandPx = layer.collisionThresholdDegrees
      ? deg2rad(clamp(layer.collisionThresholdDegrees, 0, 30)) * glyphRadius
      : 0;
    const tangentDemandPx = Math.max(labelTangentDemandPx, configuredTangentDemandPx);

    return {
      seed,
      tickAngle,
      tickLine,
      connectorRadius,
      glyphRadius,
      pointGlyphFontSize,
      tangentDemandPx,
    };
  });

  const solvedLabels = solveCircularLabelAngles(
    pointIntents.map((intent) => ({
      item: intent,
      idealAngle: intent.tickAngle,
      radius: intent.glyphRadius,
      tangentDemandPx: intent.tangentDemandPx,
    })),
    {
      paddingPx: collisionPadding,
      maxDisplacementRad: deg2rad(clamp(maxDisplacementDegrees, 0, 180)),
    }
  );
  const visualAngleByKey = Object.fromEntries(
    solvedLabels.map(({ item, visualAngle }) => [item.seed.key, visualAngle])
  );

  const points = pointIntents.map((intent) => {
    const visualAngle = visualAngleByKey[intent.seed.key] ?? intent.tickAngle;
    let glyphPosition = polarToXY(center.x, center.y, intent.glyphRadius, visualAngle);
    let point = makeAstroWheelPoint({
      layer,
      seed: intent.seed,
      tickLine: intent.tickLine,
      labelAngle: visualAngle,
      glyphPosition,
      glyphFontSize: intent.pointGlyphFontSize,
      color,
      tickColor,
    });

    const layout = getPointLabelLayout(point, scale);
    const constrainedLabel = constrainLabelInsideCircle({
      center,
      position: glyphPosition,
      layout,
      padding: collisionPadding,
      maxRadius: labelMaxRadius,
    });

    if (constrainedLabel.moved) {
      glyphPosition = constrainedLabel.position;
      point = {
        ...point,
        glyphPosition,
        leaderLine: undefined,
      };
    }

    point = {
      ...point,
      leaderArc: buildPointConnector({
        center,
        tickAngle: intent.tickAngle,
        labelAngle: visualAngle,
        connectorRadius: intent.connectorRadius,
        glyphPosition,
        mode: pointConnectorMode,
      }),
    };

    return {
      ...point,
      labelBounds: constrainedLabel.bounds,
    };
  });

  return {
    id: layer.id,
    label: layer.label,
    color,
    tickColor,
    radius,
    points,
  };
}

function makeAstroWheelPoint(params: {
  layer: AstroWheelPointLayerInput;
  seed: PointSeed;
  tickLine: AstroWheelLine;
  labelAngle: number;
  glyphPosition: AstroWheelCoordinate;
  glyphFontSize: number;
  color: string;
  tickColor: string;
}): AstroWheelPoint {
  const {
    layer,
    seed,
    tickLine,
    labelAngle,
    glyphPosition,
    glyphFontSize,
    color,
    tickColor,
  } = params;
  return {
    layerId: layer.id,
    key: seed.key,
    name: seed.name,
    kind: seed.kind,
    glyph: seed.glyph,
    glyphKey: seed.glyphKey,
    longitude: normalizeAngle(seed.longitude),
    displayLongitude: normalizeAngle(seed.longitude),
    zodiacPosition: seed.zodiacPosition,
    tickLine,
    labelAngle,
    leaderArc: undefined,
    leaderLine: undefined,
    glyphPosition,
    glyphFontSize,
    retrograde: seed.retrograde,
    color,
    tickColor,
  };
}

function buildAspectLayer(params: {
  layer: AstroWheelAspectLayerInput;
  chart: BirthChart;
  pointByKey: Record<string, AstroWheelPoint>;
  center: AstroWheelCoordinate;
  angleOf: (longitude: number) => number;
  rings: Record<AstroWheelRing["id"], AstroWheelRing>;
  palette: ResolvedAstroWheelPalette;
  excludedBodies: ReadonlySet<string>;
}): AstroWheelAspectLayer {
  const { layer, chart, pointByKey, center, angleOf, rings, palette, excludedBodies } = params;
  const sourceEdges = getSourceAspectEdges(chart, {
    enabled: true,
    edges: layer.edges,
    aspectSpecs: layer.aspectSpecs,
  });
  const aspectRadius = layer.radius ?? rings.aspects.r2 * (layer.radiusScale ?? 0.96);
  const layerPointPrefix = pointKeyPrefix(layer.pointLayerId);
  const layerPointPrefixA = layer.pointLayerIdA ? pointKeyPrefix(layer.pointLayerIdA) : layerPointPrefix;
  const layerPointPrefixB = layer.pointLayerIdB ? pointKeyPrefix(layer.pointLayerIdB) : layerPointPrefix;

  const aspectLines = sourceEdges.flatMap((edge) => {
    if (isExcludedBody(edge.planetA, excludedBodies) || isExcludedBody(edge.planetB, excludedBodies)) {
      return [];
    }

    const planetAKey = layerPointPrefixA + normalizePointKey(edge.planetA);
    const planetBKey = layerPointPrefixB + normalizePointKey(edge.planetB);
    const planetA = pointByKey[planetAKey];
    const planetB = pointByKey[planetBKey];

    if (!planetA || !planetB) {
      return [];
    }

    const spec = findAspectSpec(edge.aspect, layer.aspectSpecs);
    const maxOrb = spec?.orb ?? 8;
    const opacity = clamp((1 - edge.orb / Math.max(maxOrb, 0.001)) * (layer.opacityScale ?? 1), 0.25, 0.92);
    const a = polarToXY(center.x, center.y, aspectRadius, angleOf(planetA.longitude));
    const b = polarToXY(center.x, center.y, aspectRadius, angleOf(planetB.longitude));

    return [{
      layerId: layer.id,
      planetA: planetA.name,
      planetB: planetB.name,
      planetAKey,
      planetBKey,
      aspect: edge.aspect,
      aspectAngle: edge.aspectAngle,
      delta: edge.delta,
      orb: edge.orb,
      color: layer.color ?? layer.colors?.[edge.aspect] ?? palette.aspects[edge.aspect] ?? palette.label,
      opacity,
      line: {
        x1: a.x,
        y1: a.y,
        x2: b.x,
        y2: b.y,
      },
      strokeWidth: layer.strokeWidth ?? 1.25,
    }];
  });

  return {
    id: layer.id,
    label: layer.label,
    aspectLines,
  };
}

function getPointSeeds(
  layer: AstroWheelPointLayerInput,
  excludedBodies: ReadonlySet<string>
): PointSeed[] {
  const seeds: PointSeed[] = [];
  const chart = layer.chart;

  if (chart) {
    seeds.push(
      ...Object.entries(chart.planets).map(([key, planet]) =>
        planetSeed(key, planet, layer.id)
      )
    );
  }

  if (layer.points) {
    seeds.push(
      ...layer.points.map((point) => pointSeed(point, layer.id))
    );
  }

  if (chart && layer.nodes !== false) {
    for (const [key, node] of Object.entries(chart.nodes)) {
      seeds.push(nodeSeed(key, node, layer.id));
    }
  }

  if (chart && layer.vertex !== false && chart.houses.ascmc?.vertex) {
    seeds.push({
      key: layerPointKey(layer.id, "vertex"),
      name: "Vertex",
      kind: "vertex",
      glyph: ASTRO_WHEEL_POINT_GLYPHS.vertex,
      glyphKey: "vertex",
      longitude: chart.houses.ascmc.vertex.longitude,
      zodiacPosition: chart.houses.ascmc.vertex,
    });
  }

  return seeds
    .filter((seed) => !isExcludedBody(seed.name, excludedBodies))
    .sort((a, b) => normalizeAngle(a.longitude) - normalizeAngle(b.longitude));
}

interface PointSeed {
  key: string;
  name: string;
  kind: AstroWheelPointKind;
  glyph: string;
  glyphKey?: string;
  longitude: number;
  retrograde?: boolean;
  zodiacPosition?: ZodiacPosition;
}

interface CircularLabelItem<T> {
  item: T;
  idealAngle: number;
  radius: number;
  tangentDemandPx: number;
}

interface SolvedCircularLabelItem<T> extends CircularLabelItem<T> {
  visualAngle: number;
}

function planetSeed(key: string, planet: HydratedPlanet, layerId: string): PointSeed {
  return {
    key: layerPointKey(layerId, key || planet.name),
    name: planet.name,
    kind: "planet",
    glyph: pointGlyph(planet.name),
    glyphKey: pointGlyphKey(planet.name),
    longitude: planet.longitude,
    retrograde: (planet.longitudeSpeed ?? 0) < 0,
    zodiacPosition: planet.zodiacPosition,
  };
}

function nodeSeed(key: string, node: HydratedNode, layerId: string): PointSeed {
  return {
    key: layerPointKey(layerId, node.name || key),
    name: node.name,
    kind: "node",
    glyph: pointGlyph(node.name),
    glyphKey: pointGlyphKey(node.name),
    longitude: node.longitude,
    zodiacPosition: node,
  };
}

function pointSeed(point: AstroWheelPointSource, layerId: string): PointSeed {
  return {
    key: layerPointKey(layerId, point.key ?? point.name),
    name: point.name,
    kind: point.kind ?? "planet",
    glyph: point.glyph ?? pointGlyph(point.name),
    glyphKey: point.glyph ? undefined : pointGlyphKey(point.name),
    longitude: point.longitude,
    retrograde: point.retrograde,
    zodiacPosition: point.zodiacPosition,
  };
}

function solveCircularLabelAngles<T>(
  items: readonly CircularLabelItem<T>[],
  options: { paddingPx: number; maxDisplacementRad: number }
): SolvedCircularLabelItem<T>[] {
  const { paddingPx, maxDisplacementRad } = options;
  if (items.length === 0) {
    return [];
  }

  const normalized = items
    .map((item) => ({
      ...item,
      idealAngle: clampRad(item.idealAngle),
    }))
    .sort((a, b) => a.idealAngle - b.idealAngle);

  if (normalized.length === 1) {
    return [{ ...normalized[0], visualAngle: normalized[0].idealAngle }];
  }

  let largestGap = -Infinity;
  let seamIndex = 0;

  for (let i = 0; i < normalized.length; i++) {
    const current = normalized[i];
    const next = normalized[(i + 1) % normalized.length];
    const nextAngle = i === normalized.length - 1 ? next.idealAngle + TAU : next.idealAngle;
    const gap = nextAngle - current.idealAngle;

    if (gap > largestGap) {
      largestGap = gap;
      seamIndex = (i + 1) % normalized.length;
    }
  }

  const linear = [
    ...normalized.slice(seamIndex),
    ...normalized.slice(0, seamIndex),
  ].map((item, index) => {
    const wrapped = seamIndex > 0 && index >= normalized.length - seamIndex;
    return {
      ...item,
      idealAngle: item.idealAngle + (wrapped ? TAU : 0),
    };
  });

  for (let i = 1; i < linear.length; i++) {
    while (linear[i].idealAngle <= linear[i - 1].idealAngle) {
      linear[i] = {
        ...linear[i],
        idealAngle: linear[i].idealAngle + TAU,
      };
    }
  }

  const solved = linear.map((item) => ({
    ...item,
    visualAngle: item.idealAngle,
  }));

  for (let iteration = 0; iteration < 50; iteration++) {
    const forces = new Array(solved.length).fill(0) as number[];
    let maxForce = 0;

    for (let i = 0; i < solved.length; i++) {
      const j = (i + 1) % solved.length;
      const current = solved[i];
      const next = solved[j];
      const nextAngle = j === 0 ? next.visualAngle + TAU : next.visualAngle;
      const gap = nextAngle - current.visualAngle;
      const minGap =
        labelAngularHalfDemand(current, paddingPx) +
        labelAngularHalfDemand(next, paddingPx);

      if (gap >= minGap) {
        continue;
      }

      const push = (minGap - gap) / 2;
      forces[i] -= push;
      forces[j] += push;
      maxForce = Math.max(maxForce, push);
    }

    if (maxForce < 0.0001) {
      break;
    }

    for (let i = 0; i < solved.length; i++) {
      const movement = clamp(forces[i], -deg2rad(2), deg2rad(2));
      const moved = solved[i].visualAngle + movement;
      solved[i].visualAngle = clampAngleAroundIdeal(
        moved,
        solved[i].idealAngle,
        maxDisplacementRad
      );
    }

    solved.sort((a, b) => a.visualAngle - b.visualAngle);
    for (let i = 1; i < solved.length; i++) {
      while (solved[i].visualAngle <= solved[i - 1].visualAngle) {
        solved[i].visualAngle += TAU;
      }
    }
  }

  return solved.map((item) => ({
    ...item,
    visualAngle: clampRad(item.visualAngle),
  }));
}

function clampAngleAroundIdeal(value: number, ideal: number, maxDistance: number) {
  if (!Number.isFinite(maxDistance) || maxDistance >= Math.PI) {
    return value;
  }

  const delta = signedAngleDelta(ideal, value);
  return ideal + clamp(delta, -maxDistance, maxDistance);
}

function signedAngleDelta(from: number, to: number) {
  let delta = clampRad(to) - clampRad(from);
  if (delta > Math.PI) {
    delta -= TAU;
  } else if (delta < -Math.PI) {
    delta += TAU;
  }
  return delta;
}

function labelAngularHalfDemand<T>(
  item: Pick<CircularLabelItem<T>, "radius" | "tangentDemandPx">,
  paddingPx: number
) {
  return (item.tangentDemandPx / 2 + paddingPx) / Math.max(item.radius, 1);
}

function clampRad(value: number) {
  return ((value % TAU) + TAU) % TAU;
}

function circularAngleDistance(a: number, b: number) {
  const delta = Math.abs(clampRad(a) - clampRad(b));
  return Math.min(delta, TAU - delta);
}

function buildPointConnector(params: {
  center: AstroWheelCoordinate;
  tickAngle: number;
  labelAngle: number;
  connectorRadius: number;
  glyphPosition: AstroWheelCoordinate;
  mode: AstroWheelPointConnectorMode;
}): AstroWheelArcConnector | undefined {
  const { center, tickAngle, labelAngle, connectorRadius, glyphPosition, mode } = params;
  const displacement = circularAngleDistance(tickAngle, labelAngle);

  if (mode === "never" || (mode === "auto" && displacement < deg2rad(0.35))) {
    return undefined;
  }

  const path = arcConnectorPath({
    center,
    startAngle: tickAngle,
    endAngle: labelAngle,
    radius: connectorRadius,
    end: glyphPosition,
  });

  return {
    startAngle: tickAngle,
    endAngle: labelAngle,
    radius: connectorRadius,
    path,
  };
}

function getSourceAspectEdges(
  chart: BirthChart,
  options: ResolvedAspectOptions
): readonly AspectEdge[] {
  if (options.edges) {
    return options.edges;
  }

  if (options.aspectSpecs) {
    return computeAspectEdges(chart.planets, options.aspectSpecs);
  }

  return chart.aspects?.length
    ? chart.aspects
    : computeAspectEdges(chart.planets, ASTRO_WHEEL_DEFAULT_ASPECT_SPECS);
}

function computeAspectEdges(
  planets: Record<string, { longitude: number }>,
  specs: readonly AspectSpec[]
): AspectEdge[] {
  const keys = Object.keys(planets);
  const edges: AspectEdge[] = [];

  for (let i = 0; i < keys.length; i++) {
    for (let j = i + 1; j < keys.length; j++) {
      const planetA = planets[keys[i]];
      const planetB = planets[keys[j]];
      const match = getAspectMatch(planetA.longitude, planetB.longitude, specs);
      if (!match) {
        continue;
      }

      edges.push({
        planetA: keys[i],
        planetB: keys[j],
        longitudeA: normalizeAngle(planetA.longitude),
        longitudeB: normalizeAngle(planetB.longitude),
        aspect: match.spec.name,
        aspectAngle: match.spec.angle,
        delta: match.delta,
        orb: match.orb,
      });
    }
  }

  return edges;
}

function getAspectMatch(
  lonA: number,
  lonB: number,
  specs: readonly AspectSpec[]
): { spec: AspectSpec; orb: number; delta: number } | null {
  const delta = Math.min(
    normalizeAngle(lonB - lonA),
    normalizeAngle(lonA - lonB)
  );

  for (const spec of specs) {
    const orb = Math.abs(delta - spec.angle);
    if (orb <= spec.orb) {
      return { spec, orb, delta };
    }
  }

  return null;
}

function findAspectSpec(
  aspect: string,
  specs: readonly AspectSpec[] = ASTRO_WHEEL_DEFAULT_ASPECT_SPECS
) {
  return specs.find((spec) => spec.name === aspect);
}

function buildRings(
  outerRadius: number,
  ringFractions: AstroWheelLayoutOptions["rings"] = {}
): Record<AstroWheelRing["id"], AstroWheelRing> {
  const fractions = {
    ...DEFAULT_ASTRO_WHEEL_RING_FRACTIONS,
    ...ringFractions,
  };
  const ringMap = buildRingMap(outerRadius, [
    { id: "houses", fr: fractions.houses },
    { id: "zodiac", fr: fractions.zodiac },
    { id: "planets", fr: fractions.planets },
    { id: "aspects", fr: fractions.aspects },
  ]);

  return {
    aspects: ringMap.aspects,
    planets: ringMap.planets,
    zodiac: ringMap.zodiac,
    houses: ringMap.houses,
  };
}

function buildRingMap(
  radius: number,
  defs: readonly { id: AstroWheelRing["id"]; fr: number }[]
): Record<AstroWheelRing["id"], AstroWheelRing> {
  const normalizedDefs = defs.map((def) => ({
    ...def,
    fr: Number.isFinite(def.fr) && def.fr > 0 ? def.fr : DEFAULT_ASTRO_WHEEL_RING_FRACTIONS[def.id],
  }));
  const frTotal = normalizedDefs.reduce((sum, def) => sum + def.fr, 0);
  let cursor = radius;
  const result = {} as Record<AstroWheelRing["id"], AstroWheelRing>;

  for (const def of normalizedDefs) {
    const thickness = (radius * def.fr) / frTotal;
    const r2 = cursor;
    const r1 = cursor - thickness;
    result[def.id] = {
      id: def.id,
      r1: round(r1),
      r2: round(r2),
    };
    cursor = r1;
  }

  return result;
}

function resolvePalette(
  palette: AstroWheelPalette = "default",
  background: string | "transparent" = "transparent"
): ResolvedAstroWheelPalette {
  const base = palette === "monochrome" ? MONOCHROME_PALETTE : DEFAULT_PALETTE;
  if (palette === "default" || palette === "monochrome") {
    return {
      ...clonePalette(base),
      glyphHalo: defaultGlyphHalo(background),
    };
  }

  return {
    ...base,
    ...palette,
    glyphHalo: palette.glyphHalo ?? defaultGlyphHalo(background),
    signColors: {
      ...base.signColors,
      ...(palette.signColors ?? {}),
    },
    elementColors: {
      ...base.elementColors,
      ...(palette.elementColors ?? {}),
    },
    aspects: {
      ...base.aspects,
      ...(palette.aspects ?? {}),
    },
  };
}

function defaultGlyphHalo(background: string | "transparent") {
  return background === "transparent" ? "#fff" : background;
}

function clonePalette(palette: ResolvedAstroWheelPalette): ResolvedAstroWheelPalette {
  return {
    ...palette,
    signColors: { ...palette.signColors },
    elementColors: { ...palette.elementColors },
    aspects: { ...palette.aspects },
  };
}

function resolveZodiacOptions(options: boolean | AstroWheelZodiacOptions | undefined): ResolvedZodiacOptions {
  if (options === false) {
    return { segments: false, glyphs: false, ticks: false };
  }

  if (options === true || options === undefined) {
    return { segments: true, glyphs: true, ticks: true };
  }

  return {
    segments: options.segments ?? true,
    glyphs: options.glyphs ?? true,
    ticks: options.ticks ?? true,
  };
}

function resolveHouseOptions(options: boolean | AstroWheelHouseOptions | undefined): ResolvedHouseOptions {
  if (options === false) {
    return { labels: false, cuspLines: false, angles: false };
  }

  if (options === true || options === undefined) {
    return { labels: true, cuspLines: true, angles: true };
  }

  return {
    labels: options.labels ?? true,
    cuspLines: options.cuspLines ?? true,
    angles: options.angles ?? true,
  };
}

function resolvePointOptions(options: boolean | AstroWheelPointOptions | undefined): ResolvedPointOptions {
  if (options === false) {
    return { enabled: false, nodes: false, vertex: false };
  }

  if (options === true || options === undefined) {
    return { enabled: true, nodes: true, vertex: true };
  }

  return {
    enabled: options.enabled ?? true,
    nodes: options.nodes ?? true,
    vertex: options.vertex ?? true,
    collisionThresholdDegrees: options.collisionThresholdDegrees,
  };
}

function resolveAspectOptions(options: boolean | AstroWheelAspectOptions | undefined): ResolvedAspectOptions {
  if (options === false) {
    return { enabled: false };
  }

  if (options === true || options === undefined) {
    return { enabled: true };
  }

  return {
    enabled: options.enabled ?? true,
    edges: options.edges,
    aspectSpecs: options.aspectSpecs,
  };
}

function resolvePointLayerRadius(
  layer: AstroWheelPointLayerInput,
  rings: Record<AstroWheelRing["id"], AstroWheelRing>
) {
  const ring = rings.planets;

  if (layer.radius === "external") {
    return rings.houses.r2 + (layer.radiusOffset ?? 18);
  }

  const base = (ring.r1 + ring.r2) / 2;
  const radius = layer.radius === "inner"
    ? ring.r1
    : layer.radius === "outer"
      ? ring.r2
      : typeof layer.radius === "number"
        ? layer.radius
        : base;

  return radius + (layer.radiusOffset ?? 0);
}

function normalizeViewBox(viewBox: AstroWheelViewBox | undefined): Required<AstroWheelViewBox> {
  return {
    minX: viewBox?.minX ?? ASTRO_WHEEL_DEFAULT_VIEWBOX.minX,
    minY: viewBox?.minY ?? ASTRO_WHEEL_DEFAULT_VIEWBOX.minY,
    width: viewBox?.width ?? ASTRO_WHEEL_DEFAULT_VIEWBOX.width,
    height: viewBox?.height ?? ASTRO_WHEEL_DEFAULT_VIEWBOX.height,
  };
}

function pointGlyph(name: string) {
  return ASTRO_WHEEL_POINT_GLYPHS[normalizePointKey(name)] ?? name.charAt(0).toUpperCase();
}

function pointGlyphKey(name: string) {
  const key = normalizePointKey(name);
  return POINT_GLYPH_ALIASES[key] ?? key;
}

function resolveExcludedBodies(excludeBodies: readonly string[] | undefined) {
  return new Set((excludeBodies ?? []).map((body) => normalizePointKey(body)));
}

function isExcludedBody(name: string, excludedBodies: ReadonlySet<string>) {
  return excludedBodies.has(normalizePointKey(name));
}

function normalizePointKey(value: string) {
  return value
    .trim()
    .toLocaleLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function layerPointKey(layerId: string, value: string) {
  const key = normalizePointKey(value);
  return layerId === "birth" ? key : `${layerId}:${key}`;
}

function pointKeyPrefix(layerId: string | undefined) {
  return !layerId || layerId === "birth" ? "" : `${layerId}:`;
}

function renderGlyphPrimitives(
  push: (line: string) => void,
  primitives: readonly AstroGlyphPrimitive[],
  options: { color: string; fill?: string; glyphFill?: string }
) {
  for (const p of primitives) {
    if (p.kind === "path") {
      push(`<path d="${escapeAttr(p.d)}"/>`);
    } else if (p.kind === "circle") {
      push(`<circle cx="${fmt(p.cx)}" cy="${fmt(p.cy)}" r="${fmt(p.r)}"/>`);
    } else if (p.kind === "line") {
      push(`<line x1="${fmt(p.x1)}" y1="${fmt(p.y1)}" x2="${fmt(p.x2)}" y2="${fmt(p.y2)}"/>`);
    } else if (p.kind === "polyline") {
      push(`<polyline points="${escapeAttr(p.points)}"/>`);
    } else if (p.kind === "text") {
      push(
        `<text x="${fmt(p.x ?? 0)}" y="${fmt(p.y ?? 0)}" font-family="${textFontFamily()}" font-size="${fmt(p.fontSize ?? 0.72)}" font-weight="${escapeAttr(String(p.fontWeight ?? 500))}" text-anchor="middle" dominant-baseline="middle" fill="${escapeAttr(options.color)}" stroke="none">${escapeText(p.text)}</text>`
      );
    } else {
      let markup = p.markup;
      if (options.glyphFill) {
        markup = markup.split(GLYPH_FILL).join(options.glyphFill);
      }
      push(markup);
    }
  }
}

function normalizeAngle(value: number) {
  return clamp360(value);
}

function clamp360(value: number) {
  return ((value % 360) + 360) % 360;
}

function deg2rad(degrees: number) {
  return (degrees * Math.PI) / 180;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function polarToXY(cx: number, cy: number, radius: number, angleRad: number): AstroWheelCoordinate {
  return {
    x: round(cx + radius * Math.cos(angleRad)),
    y: round(cy + radius * Math.sin(angleRad)),
  };
}

function polarWithNudge(
  cx: number,
  cy: number,
  radius: number,
  angleRad: number,
  radialOffset = 0,
  tangentOffset = 0
): AstroWheelCoordinate {
  const x = cx + (radius + radialOffset) * Math.cos(angleRad);
  const y = cy + (radius + radialOffset) * Math.sin(angleRad);
  const tangentX = -Math.sin(angleRad);
  const tangentY = Math.cos(angleRad);

  return {
    x: round(x + tangentX * tangentOffset),
    y: round(y + tangentY * tangentOffset),
  };
}

function lineFromPolar(
  center: AstroWheelCoordinate,
  r1: number,
  r2: number,
  angleRad: number
): AstroWheelLine {
  const p1 = polarToXY(center.x, center.y, r1, angleRad);
  const p2 = polarToXY(center.x, center.y, r2, angleRad);
  return {
    x1: p1.x,
    y1: p1.y,
    x2: p2.x,
    y2: p2.y,
  };
}

function arcPath(
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  angleStart: number,
  angleEnd: number
) {
  const p0 = polarToXY(cx, cy, outerRadius, angleStart);
  const p1 = polarToXY(cx, cy, outerRadius, angleEnd);
  const p2 = polarToXY(cx, cy, innerRadius, angleEnd);
  const p3 = polarToXY(cx, cy, innerRadius, angleStart);
  const large = Math.abs(angleEnd - angleStart) % TAU > Math.PI ? 1 : 0;
  const sweep = angleEnd > angleStart ? 1 : 0;

  return [
    `M ${fmt(p0.x)},${fmt(p0.y)}`,
    `A ${fmt(outerRadius)},${fmt(outerRadius)} 0 ${large} ${sweep} ${fmt(p1.x)},${fmt(p1.y)}`,
    `L ${fmt(p2.x)},${fmt(p2.y)}`,
    `A ${fmt(innerRadius)},${fmt(innerRadius)} 0 ${large} ${sweep ? 0 : 1} ${fmt(p3.x)},${fmt(p3.y)}`,
    "Z",
  ].join(" ");
}

function arcConnectorPath(params: {
  center: AstroWheelCoordinate;
  startAngle: number;
  endAngle: number;
  radius: number;
  end: AstroWheelCoordinate;
}) {
  const { center, startAngle, endAngle, radius, end } = params;
  const start = polarToXY(center.x, center.y, radius, startAngle);
  const arcEnd = polarToXY(center.x, center.y, radius, endAngle);
  const delta = signedAngleDelta(startAngle, endAngle);
  const large = Math.abs(delta) > Math.PI ? 1 : 0;
  const sweep = delta >= 0 ? 1 : 0;
  const commands = [
    `M ${fmt(start.x)},${fmt(start.y)}`,
    `A ${fmt(radius)},${fmt(radius)} 0 ${large} ${sweep} ${fmt(arcEnd.x)},${fmt(arcEnd.y)}`,
  ];

  if (Math.hypot(arcEnd.x - end.x, arcEnd.y - end.y) > 0.5) {
    commands.push(`L ${fmt(end.x)},${fmt(end.y)}`);
  }

  return commands.join(" ");
}

function unwrapAngles(angles: readonly number[], direction: "inc" | "dec" = "inc") {
  const unwrapped: number[] = [];
  if (angles.length === 0) {
    return unwrapped;
  }

  unwrapped[0] = angles[0];
  for (let index = 1; index < angles.length; index++) {
    let angle = angles[index];
    if (direction === "inc") {
      while (angle <= unwrapped[index - 1]) {
        angle += TAU;
      }
    } else {
      while (angle >= unwrapped[index - 1]) {
        angle -= TAU;
      }
    }
    unwrapped.push(angle);
  }

  return unwrapped;
}

function round(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function fmt(value: number) {
  const rounded = round(value);
  if (Object.is(rounded, -0)) {
    return "0";
  }
  return String(rounded);
}

function escapeAttr(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeText(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function symbolFontFamily() {
  return escapeAttr("'Apple Symbols', 'Arial Unicode MS', 'Noto Sans Symbols 2', 'Noto Sans Symbols', 'Segoe UI Symbol', serif, sans-serif");
}

function textFontFamily() {
  return escapeAttr("Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif");
}
